
import React from 'react';
import { useAuth } from '@/context/SupabaseAuthContext';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, FileText, HelpCircle, Video } from 'lucide-react';

const KnowledgeBasePage: React.FC = () => {
  const { logoutUser, user } = useAuth();

  const guides = [
    {
      id: 'getting-started',
      title: 'Getting Started with Referrals',
      description: 'Learn how to set up and start using the referral system effectively.',
      icon: <BookOpen className="h-5 w-5 text-primary-blue" />
    },
    {
      id: 'tracking',
      title: 'Tracking Your Referrals',
      description: 'How to track the status and progress of your referrals.',
      icon: <FileText className="h-5 w-5 text-primary-blue" />
    },
    {
      id: 'payments',
      title: 'Understanding Payments',
      description: 'Learn about payment schedules, tracking, and history.',
      icon: <HelpCircle className="h-5 w-5 text-primary-blue" />
    },
    {
      id: 'tutorials',
      title: 'Video Tutorials',
      description: 'Visual walkthroughs of common tasks and features.',
      icon: <Video className="h-5 w-5 text-primary-blue" />
    }
  ];

  return (
    <DashboardLayout onLogout={logoutUser}>
      <div className="container mx-auto py-6 px-4">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
          <p className="text-gray-600">Find help and resources for using the platform.</p>
        </div>

        <Tabs defaultValue="guides" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="guides">Guides</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="videos">Videos</TabsTrigger>
          </TabsList>

          <TabsContent value="guides" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {guides.map((guide) => (
                <Card key={guide.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center gap-4">
                    {guide.icon}
                    <CardTitle>{guide.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{guide.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>Answers to common questions about using the referral system.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-medium">How do I send a referral?</h3>
                  <p className="text-sm text-gray-600">
                    You can send a referral by going to your dashboard and clicking "Send Referral" or by sharing your 
                    referral link directly with potential clients.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium">When will I get paid for my referrals?</h3>
                  <p className="text-sm text-gray-600">
                    Payments are processed on a monthly basis. You can view your payment history and upcoming payments
                    in the Payout Report section.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium">What happens when someone uses my referral link?</h3>
                  <p className="text-sm text-gray-600">
                    When someone uses your referral link, they'll be directed to sign up. Once they complete the process,
                    you'll receive credit for the referral and can track its status.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="videos">
            <Card>
              <CardHeader>
                <CardTitle>Video Tutorials</CardTitle>
                <CardDescription>Learn how to use the platform with our video guides.</CardDescription>
              </CardHeader>
              <CardContent className="text-center py-8">
                <Video className="h-16 w-16 mx-auto text-gray-400" />
                <p className="mt-4 text-gray-600">
                  Video tutorials coming soon! Check back for helpful visual guides.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default KnowledgeBasePage;
